import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2 } from "lucide-react";
import { api, apiErrorMessage } from "../api/client";
import { pageContainer, pageHeading, pageSubtitle, btnPrimary, btnSecondary, inputStyle, type, emptyFrame } from "../theme";
import { SheetCard, DimensionHeading } from "../components/Drawing";
import { fmtDateTime } from "../lib/format";

// Questions answered from the registers (backend lib/registerQuestions.ts). The member asks in
// words; the worker plans catalogue queries, runs them under the member's own scope and words the
// answer from those records only. Every record shown here is one the member can open themself.

interface Row {
  token: string;
  register: string;
  label: string;
  href: string;
  facts: Array<[string, string]>;
}
interface Result { tool: string; title: string; truncated: boolean; rows: Row[] }
interface Question {
  id: string;
  question: string;
  status: "queued" | "answered" | "unanswerable" | "failed";
  answer: string | null;
  results: Result[] | null;
  cited: string[];
  createdAt: string;
  answeredAt: string | null;
}

const EXAMPLES = [
  "Which processors hold special category data outside the UK?",
  "What is overdue for the board?",
  "Which open risks score 15 or more?",
  "Which critical assets are out of vendor support?",
];

const POLL_MS = 3000;
const MAX_CHARS = 500;

/** The answer with each [R3] turned into a link to the record it cites. */
function AnswerText({ text, rows }: { text: string; rows: Map<string, Row> }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(/\[(R\d+)\]/g)) {
    const row = rows.get(m[1]);
    parts.push(text.slice(last, m.index));
    parts.push(row
      ? <Link key={`${m.index}`} to={row.href} title={row.label} style={{ ...type.ref, color: "var(--c-ref)", textDecoration: "none", border: "1px solid var(--c-line)", borderRadius: 2, padding: "0 4px", margin: "0 1px" }}>{m[1]}</Link>
      : null);
    last = (m.index ?? 0) + m[0].length;
  }
  parts.push(text.slice(last));
  return <p style={{ ...type.body, margin: 0, whiteSpace: "pre-wrap" }}>{parts}</p>;
}

function Records({ results, cited }: { results: Result[]; cited: string[] }) {
  const citedSet = new Set(cited);
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {results.map((r, i) => (
        <div key={`${r.tool}-${i}`}>
          <DimensionHeading as="h3" count={r.rows.length}>{r.title}</DimensionHeading>
          {r.rows.length === 0 ? (
            <div style={type.hint}>No records.</div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
              {r.rows.map((row) => (
                <li key={row.token} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                  <span style={{ ...type.ref, color: citedSet.has(row.token) ? "var(--c-mark)" : "var(--c-faint)", minWidth: 32 }}>{row.token}</span>
                  <div style={{ minWidth: 0 }}>
                    <Link to={row.href} style={{ ...type.itemTitle, color: "var(--c-ref)", textDecoration: "none" }}>{row.label}</Link>
                    <span style={{ ...type.meta, marginLeft: 6 }}>{row.register}</span>
                    <div style={type.hint}>{row.facts.map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {r.truncated && <div style={{ ...type.meta, marginTop: 4 }}>More records matched than are listed. Open the register to see them all.</div>}
        </div>
      ))}
    </div>
  );
}

function QuestionView({ q }: { q: Question }) {
  const rows = useMemo(() => new Map((q.results ?? []).flatMap((r) => r.rows.map((row) => [row.token, row] as const))), [q.results]);
  if (q.status === "queued") return <div style={type.hint}>Reading your registers. This usually takes under a minute.</div>;
  if (q.status === "failed") return <div style={{ ...type.body, color: "var(--c-dangerText)" }}>This question could not be answered. Nothing further was charged; please try again.</div>;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {q.answer && <AnswerText text={q.answer} rows={rows} />}
      {q.results && q.results.length > 0 && <Records results={q.results} cited={q.cited} />}
    </div>
  );
}

export function AskRegistersPage() {
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState<Question[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { questions } = await api.get<{ questions: Question[] }>("/register-questions");
      setHistory(questions);
      setSelectedId((cur) => cur ?? questions[0]?.id ?? null);
    } catch (e) {
      setError(apiErrorMessage(e, "Your questions could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // While a question is being answered, refresh until it is done (it carries on if the page is left).
  const waiting = history.some((q) => q.status === "queued");
  useEffect(() => {
    if (!waiting) return;
    const t = setTimeout(() => { void load(); }, POLL_MS);
    return () => clearTimeout(t);
  }, [waiting, history, load]);

  async function ask() {
    const text = question.trim();
    if (text.length < 5) return;
    setAsking(true);
    setError(null);
    try {
      const { questionId } = await api.post<{ jobId: string; questionId: string }>("/register-questions", { question: text });
      setQuestion("");
      setSelectedId(questionId);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "The question could not be asked."));
    } finally {
      setAsking(false);
    }
  }

  async function remove(id: string) {
    try {
      await api.del(`/register-questions/${id}`);
      setHistory((h) => h.filter((q) => q.id !== id));
      if (selectedId === id) setSelectedId(null);
    } catch (e) {
      setError(apiErrorMessage(e, "The question could not be removed."));
    }
  }

  const selected = history.find((q) => q.id === selectedId) ?? null;

  return (
    <div style={pageContainer}>
      <h1 style={pageHeading}>Ask your registers</h1>
      <p style={{ ...pageSubtitle, maxWidth: 760 }}>
        Ask a question in your own words. The answer is built from your organisation's registers, only from records you can open yourself, and every record it names links to it.
        The AI chooses which registers to read and words the answer; it never reads your database directly.
      </p>

      <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
        <SheetCard label="Ask a question">
          <label htmlFor="ask-question" style={type.label}>Your question</label>
          <textarea
            id="ask-question"
            value={question}
            maxLength={MAX_CHARS}
            rows={3}
            placeholder="For example: which processors hold special category data outside the UK?"
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void ask(); }}
            style={{ ...inputStyle, width: "100%", resize: "vertical", fontFamily: "inherit" }}
          />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 8 }}>
            <button type="button" style={{ ...btnPrimary, display: "inline-flex", gap: 6, alignItems: "center" }} disabled={asking || question.trim().length < 5} onClick={() => void ask()}>
              <Search size={14} /> {asking ? "Asking…" : "Ask"}
            </button>
            <span style={type.meta}>{question.length} / {MAX_CHARS}</span>
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={type.eyebrow}>Try</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {EXAMPLES.map((ex) => (
                <button key={ex} type="button" style={{ ...btnSecondary, fontSize: 12, padding: "4px 8px" }} onClick={() => setQuestion(ex)}>{ex}</button>
              ))}
            </div>
          </div>
          {error && <div role="alert" style={{ ...type.body, color: "var(--c-dangerText)", marginTop: 10 }}>{error}</div>}
        </SheetCard>

        {selected && (
          <SheetCard label="Answer" reference={fmtDateTime(selected.createdAt)}>
            <div style={{ ...type.itemTitle, marginBottom: 10 }}>{selected.question}</div>
            <QuestionView q={selected} />
          </SheetCard>
        )}

        <SheetCard label="Your earlier questions" reference={history.length ? String(history.length) : undefined}>
          {loading ? (
            <div style={type.hint}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ ...emptyFrame, padding: 16 }}><span style={type.hint}>Questions you ask appear here. Only you can see them.</span></div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 4 }}>
              {history.map((q) => (
                <li key={q.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(q.id)}
                    style={{ flex: 1, minWidth: 0, textAlign: "left", background: "none", border: "none", padding: "4px 0", cursor: "pointer", ...type.body, color: q.id === selectedId ? "var(--c-mark)" : "var(--c-text)", fontWeight: q.id === selectedId ? 600 : 400 }}
                  >
                    {q.question}
                  </button>
                  <span style={type.meta}>{q.status === "queued" ? "Answering…" : fmtDateTime(q.createdAt)}</span>
                  {q.status !== "queued" && (
                    <button type="button" aria-label="Remove from your history" title="Remove from your history" onClick={() => void remove(q.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-faint)", padding: 2 }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </SheetCard>
      </div>
    </div>
  );
}
